// SnakeLadderBoard — premium 10x10 board with ladders, snakes, square-type icons, and 2 player pieces.
import React from 'react';
import { motion } from 'framer-motion';
import { Star, Crown, ArrowUp, Lightbulb } from 'lucide-react';
import { BOARD_SIZE, LADDERS, SNAKES, KNOWLEDGE_SQUARES, BOSS_SQUARES, cellCoord, squareType, COLOR_THEMES } from './snakeLadderEngine';

// Build cell grid: rows 0..9 (top→bottom). For each row, list cells L→R.
function buildGrid() {
    const grid = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
        const rowFromBottom = BOARD_SIZE - 1 - row;
        const cells = [];
        for (let col = 0; col < BOARD_SIZE; col++) {
            const goingRight = rowFromBottom % 2 === 0;
            const colInRow = goingRight ? col : BOARD_SIZE - 1 - col;
            const n = rowFromBottom * BOARD_SIZE + colInRow + 1;
            cells.push(n);
        }
        grid.push(cells);
    }
    return grid;
}

const GRID = buildGrid();

const CELL_BG = {
    plain:     { bg: 'rgba(255,255,255,0.05)',  border: 'rgba(255,255,255,0.06)' },
    knowledge: { bg: 'rgba(56,189,248,0.22)',   border: 'rgba(56,189,248,0.55)' },
    boss:      { bg: 'rgba(168,85,247,0.28)',   border: 'rgba(217,70,239,0.7)'  },
    ladderBot: { bg: 'rgba(16,185,129,0.30)',   border: 'rgba(16,185,129,0.7)'  },
    snakeHead: { bg: 'rgba(244,63,94,0.30)',    border: 'rgba(244,63,94,0.75)'  },
};

function classifyCell(n) {
    if (LADDERS[n] !== undefined) return 'ladderBot';
    if (SNAKES[n] !== undefined) return 'snakeHead';
    if (BOSS_SQUARES.includes(n)) return 'boss';
    if (KNOWLEDGE_SQUARES.includes(n)) return 'knowledge';
    return 'plain';
}

function CellIcon({ n }) {
    if (LADDERS[n] !== undefined) return <ArrowUp className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-200" />;
    if (SNAKES[n] !== undefined) return <span className="text-rose-300 text-[10px] sm:text-xs font-extrabold">🐍</span>;
    if (BOSS_SQUARES.includes(n)) return <Crown className="h-3 w-3 sm:h-4 sm:w-4 text-fuchsia-200" />;
    if (KNOWLEDGE_SQUARES.includes(n)) return <Star className="h-3 w-3 sm:h-4 sm:w-4 text-sky-200" />;
    return null;
}

// Render ladders (green diagonal beams) and snakes (red curved lines) as SVG overlays.
function LaddersAndSnakes({ cellSizePct }) {
    // cellSizePct = 10 (10% of 100)
    const center = (n) => {
        const [r, c] = cellCoord(n);
        return { x: (c + 0.5) * cellSizePct, y: (r + 0.5) * cellSizePct };
    };
    return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden>
            <defs>
                <linearGradient id="ladderGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fde047" />
                    <stop offset="100%" stopColor="#facc15" />
                </linearGradient>
                <linearGradient id="snakeBody" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#65a30d" />
                    <stop offset="50%" stopColor="#4d7c0f" />
                    <stop offset="100%" stopColor="#365314" />
                </linearGradient>
                <linearGradient id="snakeBelly" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fef08a" />
                    <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
                <filter id="ladderShadow"><feDropShadow dx="0" dy="0.3" stdDeviation="0.4" floodColor="#000" floodOpacity="0.45"/></filter>
                <filter id="snakeShadow"><feDropShadow dx="0.2" dy="0.4" stdDeviation="0.3" floodColor="#000" floodOpacity="0.55"/></filter>
            </defs>
            {/* Ladders */}
            {Object.entries(LADDERS).map(([bot, top]) => {
                const a = center(Number(bot));
                const b = center(Number(top));
                const dx = b.x - a.x, dy = b.y - a.y;
                const len = Math.sqrt(dx * dx + dy * dy);
                const px = -dy / len * 1.6;
                const py =  dx / len * 1.6;
                // Two rails + rungs
                const rungs = [];
                const steps = Math.max(4, Math.floor(len / 4));
                for (let i = 1; i < steps; i++) {
                    const t = i / steps;
                    const cx = a.x + dx * t;
                    const cy = a.y + dy * t;
                    rungs.push(
                        <line key={`r-${bot}-${i}`} x1={cx + px} y1={cy + py} x2={cx - px} y2={cy - py}
                            stroke="url(#ladderGrad)" strokeWidth={0.55} strokeLinecap="round" filter="url(#ladderShadow)" />
                    );
                }
                return (
                    <g key={`ladder-${bot}`}>
                        <line x1={a.x + px} y1={a.y + py} x2={b.x + px} y2={b.y + py}
                            stroke="url(#ladderGrad)" strokeWidth={0.7} strokeLinecap="round" filter="url(#ladderShadow)" />
                        <line x1={a.x - px} y1={a.y - py} x2={b.x - px} y2={b.y - py}
                            stroke="url(#ladderGrad)" strokeWidth={0.7} strokeLinecap="round" filter="url(#ladderShadow)" />
                        {rungs}
                    </g>
                );
            })}
            {/* Snakes — proper snake body with slithering S-curve, scales, head with eyes + forked tongue */}
            {Object.entries(SNAKES).map(([head, tail], snakeIdx) => {
                const a = center(Number(head));   // Head position
                const b = center(Number(tail));   // Tail position
                const dx = b.x - a.x, dy = b.y - a.y;
                const len = Math.sqrt(dx * dx + dy * dy);
                // Two control points create S-curve slither
                const perpX = -dy / len;
                const perpY =  dx / len;
                const amp = Math.min(8, len * 0.18);
                const c1x = a.x + dx * 0.33 + perpX * amp;
                const c1y = a.y + dy * 0.33 + perpY * amp;
                const c2x = a.x + dx * 0.66 - perpX * amp;
                const c2y = a.y + dy * 0.66 - perpY * amp;
                const bodyPath = `M ${a.x} ${a.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${b.x} ${b.y}`;
                // Scales — circles along the body at curve samples
                const scales = [];
                const numScales = Math.max(6, Math.floor(len / 3));
                for (let i = 1; i < numScales; i++) {
                    const t = i / numScales;
                    // Quadratic Bezier sample (approximation of cubic via two segments)
                    const omt = 1 - t;
                    const sx = omt*omt*omt*a.x + 3*omt*omt*t*c1x + 3*omt*t*t*c2x + t*t*t*b.x;
                    const sy = omt*omt*omt*a.y + 3*omt*omt*t*c1y + 3*omt*t*t*c2y + t*t*t*b.y;
                    scales.push({ x: sx, y: sy, r: 0.35 + Math.sin(i) * 0.05 });
                }
                // Head direction
                const headDx = c1x - a.x;
                const headDy = c1y - a.y;
                const headLen = Math.sqrt(headDx*headDx + headDy*headDy) || 1;
                const hux = headDx / headLen;
                const huy = headDy / headLen;
                // Tongue tip extends FORWARD from head (opposite of where body goes)
                const tongueDir = -1;
                const tongueX = a.x + tongueDir * hux * 2.6;
                const tongueY = a.y + tongueDir * huy * 2.6;
                const tongueF1x = tongueX + (-huy) * 0.7;
                const tongueF1y = tongueY + ( hux) * 0.7;
                const tongueF2x = tongueX - (-huy) * 0.7;
                const tongueF2y = tongueY - ( hux) * 0.7;
                // Eyes — perpendicular to head direction
                const eyeOff = 0.7;
                const eyeBackX = a.x + hux * 0.4;
                const eyeBackY = a.y + huy * 0.4;
                const eL = [eyeBackX + (-huy) * eyeOff, eyeBackY + hux * eyeOff];
                const eR = [eyeBackX - (-huy) * eyeOff, eyeBackY - hux * eyeOff];
                return (
                    <g key={`snake-${head}`} filter="url(#snakeShadow)">
                        {/* Body — main green */}
                        <path d={bodyPath} stroke="url(#snakeBody)" strokeWidth={2.6} fill="none" strokeLinecap="round" />
                        {/* Body — yellow belly underneath as inner stroke */}
                        <path d={bodyPath} stroke="url(#snakeBelly)" strokeWidth={1.0} fill="none" strokeLinecap="round" opacity="0.55"
                            strokeDasharray="0.6 0.6" />
                        {/* Scale dots */}
                        {scales.map((s, i) => (
                            <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#365314" opacity="0.7" />
                        ))}
                        {/* Forked tongue (red) */}
                        <line x1={a.x - hux * 1.2} y1={a.y - huy * 1.2} x2={tongueX} y2={tongueY} stroke="#dc2626" strokeWidth={0.4} />
                        <line x1={tongueX} y1={tongueY} x2={tongueF1x} y2={tongueF1y} stroke="#dc2626" strokeWidth={0.35} strokeLinecap="round" />
                        <line x1={tongueX} y1={tongueY} x2={tongueF2x} y2={tongueF2y} stroke="#dc2626" strokeWidth={0.35} strokeLinecap="round" />
                        {/* Head — bigger ellipse with darker rim */}
                        <ellipse cx={a.x} cy={a.y} rx={2.0} ry={1.6}
                            transform={`rotate(${Math.atan2(huy, hux) * 180 / Math.PI} ${a.x} ${a.y})`}
                            fill="#4d7c0f" stroke="#1a2e05" strokeWidth={0.25} />
                        {/* Eyes — white sclera */}
                        <circle cx={eL[0]} cy={eL[1]} r={0.45} fill="#fffbeb" />
                        <circle cx={eR[0]} cy={eR[1]} r={0.45} fill="#fffbeb" />
                        {/* Pupils — slit (vertical) */}
                        <ellipse cx={eL[0]} cy={eL[1]} rx={0.12} ry={0.32} fill="#000" />
                        <ellipse cx={eR[0]} cy={eR[1]} rx={0.12} ry={0.32} fill="#000" />
                        {/* Tail tip — pointed */}
                        <circle cx={b.x} cy={b.y} r={0.7} fill="#365314" />
                    </g>
                );
            })}
        </svg>
    );
}

// 3D player pin — yellow or red.
function PlayerPin({ color, label, isPlayer }) {
    const c = COLOR_THEMES[color];
    return (
        <motion.div
            layout
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 240, damping: 18 }}
            className="absolute pointer-events-none flex items-center justify-center"
            style={{
                width: '70%', height: '70%',
                left: isPlayer ? '5%' : '25%',
                top: '10%',
                zIndex: 30,
            }}
        >
            <div className="relative w-full h-full">
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 30% 25%, #fff 0%, ${c.primary} 35%, ${c.dark} 90%)`,
                        boxShadow: `0 4px 8px rgba(0,0,0,0.55), inset 0 -3px 4px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.6)`,
                        border: `1.5px solid ${c.dark}`,
                    }}
                />
                <div className="absolute inset-0 flex items-center justify-center font-display font-extrabold text-[10px] sm:text-xs"
                    style={{ color: c.text, textShadow: '0 1px 1px rgba(0,0,0,0.3)' }}>
                    {label}
                </div>
            </div>
        </motion.div>
    );
}

export default function SnakeLadderBoard({ players, lastJump }) {
    return (
        <div className="relative w-full max-w-[640px] mx-auto aspect-square rounded-3xl p-2 sm:p-3"
            style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
                boxShadow: 'inset 0 0 40px rgba(0,0,0,0.6), 0 20px 50px rgba(0,0,0,0.5)',
                border: '2px solid rgba(255,255,255,0.07)',
            }}
            data-testid="sl-board"
        >
            <div className="relative w-full h-full rounded-2xl overflow-hidden">
                {/* Grid */}
                <div className="grid grid-cols-10 grid-rows-10 w-full h-full">
                    {GRID.flat().map((n) => {
                        const kind = classifyCell(n);
                        const style = CELL_BG[kind];
                        const isEven = (Math.floor((n - 1) / BOARD_SIZE) + ((n - 1) % BOARD_SIZE)) % 2 === 0;
                        return (
                            <div key={n} className="relative" style={{
                                background: isEven ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.15)',
                                borderRight: '1px solid rgba(255,255,255,0.04)',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                            }}>
                                <div className="absolute inset-0.5 rounded-md" style={{ background: style.bg, border: `1px solid ${style.border}` }} />
                                <div className="absolute top-0.5 left-0.5 sm:top-1 sm:left-1 text-[7px] sm:text-[9px] font-extrabold text-white/70 leading-none">{n}</div>
                                <div className="absolute bottom-0.5 right-0.5 sm:bottom-1 sm:right-1">
                                    <CellIcon n={n} />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Ladders + snakes */}
                <LaddersAndSnakes cellSizePct={10} />

                {/* Player pins */}
                {Object.entries(players).map(([color, pos]) => {
                    if (pos < 1) return null;
                    const [r, c] = cellCoord(pos);
                    return (
                        <motion.div
                            key={color}
                            layout
                            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                            className="absolute"
                            style={{
                                left: `${c * 10}%`,
                                top: `${r * 10}%`,
                                width: '10%', height: '10%',
                            }}
                        >
                            <PlayerPin color={color} label={COLOR_THEMES[color].name.split(' ')[0]} isPlayer={color === 'yellow'} />
                        </motion.div>
                    );
                })}

                {/* Last jump celebration arc — flashes when ladder climbed */}
                {lastJump && (
                    <motion.div
                        key={lastJump.id}
                        initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.4 }}
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: lastJump.kind === 'ladder'
                                ? 'radial-gradient(circle at center, rgba(16,185,129,0.35) 0%, transparent 60%)'
                                : 'radial-gradient(circle at center, rgba(244,63,94,0.35) 0%, transparent 60%)',
                        }}
                    />
                )}
            </div>
        </div>
    );
}
